import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { portalServer } from "../config";
import {
  useSyncData,
  useSyncModuleRegistration,
  type SyncModule,
} from "../sync";
import type { Information } from "../types";
import { limiter } from "./demo";
import { daily } from "./provider";

const syncModuleId = "informationen";

function useInformationenSyncModule(): SyncModule<ReadonlyArray<Information>> {
  const { portalKey, demo } = useAuthenticated();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: {
        schedule: { type: "periodic", interval: daily },
      },
      fetchData: async () => {
        const response = await fetch(
          `${portalServer}/get-informations.php?ORG_ACCESSKEY=${encodeURIComponent(
            portalKey,
          )}`,
        );

        if (!response.ok) {
          throw new Error(
            `Error fetching data: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        const limited = limiter<Information>(demo, 3);
        return limited(data.INFORMATION_LIST ?? []).map(parseInformation);
      },
    }),
    [demo, portalKey],
  );
}

export function useInformationenSync() {
  const module = useInformationenSyncModule();
  useSyncModuleRegistration(module);
}

export function useInformationen(): ReadonlyArray<Information> {
  return useSyncData(syncModuleId) ?? [];
}

function parseInformation(i: any): Information {
  return {
    id: i.INFORMATION_ID,
    text: i.INFORMATION_TEXT,
    von: i.INFORMATION_VALID_FROM,
    bis: i.INFORMATION_VALID_TO,
  };
}
